import React from "react";
import {
  Box,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import StandardButton from "../../../../components/StandardButton";
import {
  Quiz as QuestionIcon,
  TrendingUp as GraphIcon,
} from "@mui/icons-material";
import EmptyStateCard from "../../../../components/EmptyStateCard";
import useEvents from "../../../../hooks/useEvents";

type EmptyReportQuestionsProps = {
  onAddQuestion: () => void;
};

const EmptyReportQuestions = ({ onAddQuestion }: EmptyReportQuestionsProps) => {
  const events = useEvents();

  return (
    <Box data-testid="EmptyReportQuestionsView" style={{ margin: 24 }}>
      <EmptyStateCard
        text="Set up custom report questions to gather specific information from your clients' weekly progress reports."
      />
      <Box
        style={{
          marginTop: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack style={{ maxWidth: 500, alignItems: "center" }} spacing={2}>
          <Typography style={{ textAlign: "center", marginBottom: 16 }}>
            Create custom questions that clients will answer when submitting
            their weekly reports. You can ask about energy levels, mood,
            challenges, measurements, or any other metrics important to their
            progress.
          </Typography>

          {/* Data Types Information Table */}
          <Box style={{ marginBottom: 24, maxWidth: 600 }}>
            <Typography
              variant="subtitle2"
              style={{ marginBottom: 12, fontWeight: "600" }}
            >
              Supported Question Types:
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Description</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Best For</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Multiline</TableCell>
                    <TableCell>Free-form text input</TableCell>
                    <TableCell>Challenges, goals, notes</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Number</TableCell>
                    <TableCell>Numeric input</TableCell>
                    <TableCell>Weight, measurements, reps</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Rating</TableCell>
                    <TableCell>Star rating (1-5)</TableCell>
                    <TableCell>Energy levels, satisfaction</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Scale</TableCell>
                    <TableCell>Slider scale (custom range)</TableCell>
                    <TableCell>Pain levels, mood scale</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Yes/No</TableCell>
                    <TableCell>Boolean true/false</TableCell>
                    <TableCell>Compliance, achievements</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <GraphIcon sx={{ fontSize: 14, mr: 1.0 }} />
            <Typography variant="caption" color="text.secondary">
              Pro tip: Number, Rating, Scale, and Yes/No questions create
              graphable data for tracking trends over time
            </Typography>
          </Box>

          <StandardButton
            data-testid="EmptyReportQuestionsView-Button"
            variant="primary"
            startIcon={<QuestionIcon />}
            onClick={() => {
              onAddQuestion();
              events.logButtonClick({
                buttonName: "Add Your First Report Question",
                pageName: "Admin",
                tabName: "Report Questions",
              });
            }}
            style={{ fontSize: 16 }}
          >
            Add Your First Report Question
          </StandardButton>
        </Stack>
      </Box>
    </Box>
  );
};

export default EmptyReportQuestions;
